import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { ProjectStorage } from '../../core/project.storage';
import { DcBaseRequester } from '../core/dc-base.requester';
import {IEntityResponse, IEntity, IEntityPersistenceStateResponse} from './entity.model';

@Injectable({
	providedIn: 'root',
})
export class EntityRequester extends DcBaseRequester<IEntityResponse> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;
	public constructor(
		private readonly urlService: UrlService,
		private readonly userSettings: ProjectStorage
	) {
		super();
		this.resource = this.generateResourceUrl('ENTITES_GET_ALL_DETAILED');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getEntities(
		includePublic: boolean,
		useCache = true
	): Observable<ReadonlyArray<IEntityResponse>> {
		const url = this.urlService.generateUrl(
			this.resource,
			{},
			{
				project: this.projectId,
				excludePublic: !includePublic,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  });
		return this.get<Array<IEntityResponse>>(url, cachePolicy);
	}

	public getEntitiesFrameStatus(
		ids: Array<number>,
		useCache: boolean,
	): Observable<ReadonlyArray<IEntityPersistenceStateResponse>> {
		const resourceUrl = this.generateResourceUrl(
			'ENTITES_FRAME_STATUS'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				entitesId: ids,
			}
		);
		return this.get<ReadonlyArray<IEntityPersistenceStateResponse>>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				evictTimeoutInSeconds: 2,
				isCacheable: true,
			})
		);
	}

	public deleteEntity(id: number): Observable<void> {
		const resourceUrl = this.generateResourceUrl('ENTITES_DELETE_ONE');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id,
			},
			{
				project: this.projectId,
				ctx: this.userSettings.getSelectedContext(),
			}
		);
		return this.http.delete<void>(url);
	}

	public deleteMany(ids: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('ENTITES_DELETE_BULK');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				ctx: this.userSettings.getSelectedContext(),
				selectedUets: ids,
			}
		);
		return this.http.delete<void>(url);
	}

	public createDatablocksFromEntities(ids: Array<number>): Observable<void> {
		const resourceUrl = this.generateResourceUrl('DATABLOCKS_CREATE_BY_UETS');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);
		return this.http.post<void>(url, ids);
	}

	public getEntity(id: number): Observable<IEntity> {
		const resourceUrl = this.generateResourceUrl('ENTITES_GET');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IEntity>(url);
	}

	public updateEntity(entity: IEntity): Observable<void> {
		const resourceUrl = this.generateResourceUrl('ENTITES_EDIT');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, entity);
	}

	public getDepotCountByEntityId(entityId: number): Observable<number> {
		const resourceUrl = this.generateResourceUrl(
			'ENTITE_DEPOT_LINKS_BY_UET_COUNT'
		);
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: entityId,
			},
			{
				project: this.projectId,
			}
		);
		return this.get<number>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				evictTimeoutInSeconds: 60,
				isCacheable: true,
			})
		);
	}

	public getCharacteristicCountByEntityId(
		entityId: number
	): Observable<number> {
		const resourceUrl = this.generateResourceUrl('ENTITES_CARAC_COUNT');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: entityId,
			},
			{
				project: this.projectId,
			}
		);
		return this.get<number>(
			url,
			CachePolicyEntity.build({
				policy: CachePolicy.CacheOnly,
				evictTimeoutInSeconds: 60,
				isCacheable: true,
			})
		);
	}

	public stopPersistence(jobId: string): Observable<void> {
		const resourceUrl = this.generateResourceUrl('SPARK_JOB');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				jobKey: jobId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.delete<void>(url);
	}
}
